import { DateTime, formatToYMD, getDateTime } from 'infras/datetime';
import {
  DragEvent,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  CalendarWeekDay,
  EventListItem,
  Frame,
  MergeableEventListItem,
  Schedule,
  ScheduleType,
  SelectedSlot,
} from 'presentations/views/components/atoms/Calendar/reducer';
import {
  CalendarContext,
  setCreatingEvent,
  setSchedules,
  setSelectedGroups,
  setSelectedSlots,
} from 'presentations/views/components/atoms/Calendar/reducer/CalendarContext';
import {
  getPersistentItem,
  setPersistentItem,
  defaultCacheExpiration,
} from 'presentations/views/helpers/storage';
import { ProviderType } from 'domains/models/calendar/providerType';

interface Props {
  frames: Frame[];
  dates: DateTime[];
  startedAt: DateTime;
  onUpdateSchedule?: (schedule: Schedule) => void;
}

export function useEvent(props: Props) {
  const { state, dispatch } = useContext(CalendarContext);

  const [
    draggingGhostEvent,
    setDraggingGhostEvent,
  ] = useState<MergeableEventListItem | null>(null);
  const [events, setEvents] = useState<MergeableEventListItem[]>([]);
  const [
    draggingEvent,
    setDraggingEvent,
  ] = useState<MergeableEventListItem | null>(null);
  const [dragDirection, setDragDirection] = useState<'bottom' | null>(null);
  const [isDropped, setIsDropped] = useState<boolean>(false);

  const cache = useMemo(() => {
    const gs = props.startedAt.toMillis();
    const schedules: Schedule[] =
      getPersistentItem(
        `${state.cachePrefix}:schedules-day-${state.showRange.day}-${gs}`,
      ) || [];
    return { schedules: schedules || null };
  }, [props.startedAt, state.showRange, state.cachePrefix]);

  const isActive = [
    state.currentPage - 1,
    state.currentPage,
    state.currentPage + 1,
  ].includes(state.pages.indexOf(props.startedAt));

  const isUseCache = state.schedules === null || !isActive;

  const currentSchedules = useMemo(() => {
    if (!state.schedules) {
      return [];
    }
    const gridStartedAt = props.startedAt;
    const gridEndedAt = gridStartedAt.plus(state.showRange);
    const currents = [];
    for (let i = 0; i < state.schedules.length; i++) {
      const current = state.schedules[i];
      if (current.isAllDay) continue;
      const startedAt = getDateTime(
        current.startedAt,
        current.timeZone,
      ).setZone(state.timeZone);

      const endedAt = getDateTime(current.endedAt, current.timeZone).setZone(
        state.timeZone,
      );
      const isOverlap = gridStartedAt < endedAt && gridEndedAt >= startedAt;
      if (!isOverlap) continue;
      currents.push(current);
    }
    return currents;
  }, [state.schedules, cache, props.startedAt, state.showRange]);

  const schedules = isUseCache ? cache.schedules : currentSchedules;

  const calcX = () => {};

  useEffect(() => {
    if (!schedules) {
      return;
    }
    const eventListItems = [];
    const searchSchedules = [...schedules];
    const gridStartedAt = props.startedAt;
    const gridEndedAt = gridStartedAt.plus(state.showRange);
    if (state.creatingEvent) {
      const isOverlap =
        gridStartedAt < getDateTime(state.creatingEvent.endedAt) &&
        gridEndedAt >= getDateTime(state.creatingEvent.startedAt);
      if (isOverlap) {
        searchSchedules.push(state.creatingEvent);
      }
    }
    const oneMinute = 1000 * 60;
    const oneDay = oneMinute * 60 * 24;
    const durationDivider = oneMinute * state.rulerStep;

    const gs = gridStartedAt.toMillis();
    const ge = gridEndedAt.toMillis();

    // 時間変換まわりの処理をまとめて行う
    // 中間処理した方がパフォーマンス的に速いかと思うのと、
    // 比較処理がシンプルになるのでやっているが不要ならまとめる
    for (let i = 0; i < searchSchedules.length; i++) {
      const current = searchSchedules[i];
      if (current.isAllDay) continue;
      const startedAt = getDateTime(
        current.startedAt,
        current.timeZone,
      ).setZone(state.timeZone);

      const weekday = (startedAt.weekday % 7) as CalendarWeekDay;
      if (state.hiddenWeekdays.includes(weekday)) continue;

      const endedAt = getDateTime(current.endedAt, current.timeZone).setZone(
        state.timeZone,
      );

      const listItem = {
        scheduleType: current.scheduleType,
        uid: current.calendarId
          ? `${current.calendarId}-${current.uid}`
          : current.isRecurringEvent &&
            current.providerType === ProviderType.Jicoo
          ? `${current.uid}-${current.startedAt}`
          : current.uid,
        calendarId: current.calendarId,
        eventId: current.uid,
        isDraggable: true,
        name: current.name || '',
        isAllDay: current.isAllDay,
        color: current.color,
        userId: current.userId,
        url: current.url,
        status: current.status,
        responseStatus: current.responseStatus,
        providerType: current.providerType,
        calendarSource: current.calendarSource,
        description: current.description,
        address: current.address,
        isRecurringEvent: current.isRecurringEvent,
        behavior: current.behavior,
        customData: current.customData,
        xRatio: 0,
        xSpanRatio: 1,
        startedAt,
        endedAt,
      };

      // 日またがり
      if (formatToYMD(startedAt) !== formatToYMD(endedAt)) {
        let currentStartedAt = startedAt.plus({ hour: 0 });
        for (let j = 1; ; j++) {
          let currentEndedAt = currentStartedAt.plus({ day: 1 }).startOf('day');
          if (endedAt < currentEndedAt) {
            currentEndedAt = endedAt;
          }
          const cs = currentStartedAt.toMillis();
          const ce = currentEndedAt.toMillis();
          const isOverlap = gs < ce && ge >= cs;
          if (isOverlap) {
            const ySpan = Math.ceil((ce - cs) / durationDivider);
            let x = Math.floor((cs - gs) / oneDay);
            if (state.hiddenWeekdays.length > 0) {
              for (
                let s = gridStartedAt;
                s < startedAt;
                s = s.plus({ milliseconds: oneDay })
              ) {
                const weekday = (s.weekday % 7) as CalendarWeekDay;
                if (state.hiddenWeekdays.includes(weekday)) {
                  x--;
                }
              }
            }
            const y =
              Math.floor(
                (cs -
                  currentStartedAt
                    .startOf('day')
                    .set({ hour: state.rulerMinHour })
                    .toMillis()) /
                  durationDivider,
              ) || 0;

            const event: EventListItem = {
              ...listItem,
              layoutId: `${current.userId}-${current.uid}-${j}-${i}`,
              x,
              y,
              ySpan: ySpan < 1 ? 1 : Math.ceil(ySpan),
            };
            if (x >= 0) {
              eventListItems.push(event);
            }
          }
          currentStartedAt = startedAt.plus({ day: j }).startOf('day');
          if (currentStartedAt.toMillis() >= endedAt.toMillis()) {
            break;
          }
        }
      } else {
        const cs = startedAt.toMillis();
        const ce = endedAt.toMillis();
        const ySpan = Math.ceil((ce - cs) / durationDivider);
        let x = Math.floor((cs - gs) / oneDay);
        if (state.hiddenWeekdays.length > 0) {
          for (
            let s = gridStartedAt;
            s < startedAt;
            s = s.plus({ milliseconds: oneDay })
          ) {
            const weekday = (s.weekday % 7) as CalendarWeekDay;
            if (state.hiddenWeekdays.includes(weekday)) {
              x--;
            }
          }
        }

        const event: EventListItem = {
          ...listItem,
          layoutId: `${current.userId}-${current.uid}-${i}`,
          x,
          y: Math.floor(
            (cs -
              startedAt
                .startOf('day')
                .set({ hour: state.rulerMinHour })
                .toMillis()) /
              durationDivider,
          ),
          ySpan: ySpan < 1 ? 1 : Math.ceil(ySpan),
        };
        eventListItems.push(event);
      }
    }

    const sortedEventListItems = eventListItems.sort((a, b) => {
      if (a.ySpan && b.ySpan) {
        if (a.ySpan > b.ySpan) return -1;
      }
      return 0;
    });

    // イベントのマージ
    let eventsCount = sortedEventListItems.length;
    const mergedListItems: MergeableEventListItem[] = [];
    if (state.isShowSameEventMerge) {
      const mergedEventIds: string[] = [];
      for (let i = 0; i < eventsCount; i++) {
        const event = sortedEventListItems[i];
        // Jicooの繰り返しイベントはIDがユニークでないため
        // ユニークIDを触れるようになったらxのsuffixは削除できる
        const uniqEventId = `${event.eventId}-${event.x}`;
        if (mergedEventIds.includes(uniqEventId)) {
          continue;
        }
        const sameLayoutEvents: EventListItem[] = [];
        for (let j = i + 1; j < eventsCount; j++) {
          const targetEvent = sortedEventListItems[j];
          if (targetEvent.scheduleType === ScheduleType.Availability) continue;

          if (
            event.x === targetEvent.x &&
            event.eventId === targetEvent.eventId &&
            event.startedAt.toMillis() === targetEvent.startedAt.toMillis()
          ) {
            sameLayoutEvents.push(targetEvent);
          }
        }
        mergedListItems.push({ ...event, mergedEvents: sameLayoutEvents });
        if (sameLayoutEvents.length > 0) {
          mergedEventIds.push(uniqEventId);
        }
      }
    }

    // イベントのレイアウト
    const layoutedListItems: MergeableEventListItem[] = state.isShowSameEventMerge
      ? [...mergedListItems]
      : [...sortedEventListItems];
    eventsCount = layoutedListItems.length;
    for (let i = 0; i < eventsCount; i++) {
      const event = layoutedListItems[i];
      let override = 0;

      const spanMaps: any = {};
      const positionMaps: any = {};
      const spanRatioMaps: any = {};
      const ratioMaps: any = {};
      let hasNextEvent = false;

      for (let j = 0; j < eventsCount; j++) {
        const targetEvent = layoutedListItems[j];
        if (targetEvent.isAllDay) continue;
        if (targetEvent.scheduleType === ScheduleType.Availability) continue;

        if (event.layoutId === targetEvent.layoutId) {
          continue;
        }

        const cs = event.y;
        const ts = targetEvent.y;
        const te = targetEvent.y + targetEvent.ySpan;
        const ce = event.y + event.ySpan;
        const isOverlap = cs < te && ce > ts;

        if (event.x === targetEvent.x && isOverlap) {
          const isAllowOverride = state.isShowEventOverlap
            ? Math.abs(ts - cs) >= 2
            : false;
          if (isAllowOverride) {
            if (i > j) {
              override++;
            }
          } else {
            // 各y値で重なりを取得して最終的に最も最適なものをspan, positionとして採用する
            for (let y = cs; y < ce; y++) {
              if (ts <= y && y < te) {
                spanMaps[y] = !spanMaps[y] ? 1 : spanMaps[y] + 1;
                if (i > j) {
                  positionMaps[y] = !positionMaps[y] ? 1 : positionMaps[y] + 1;
                  spanRatioMaps[y] = targetEvent.xSpanRatio;
                  ratioMaps[y] = targetEvent.xRatio;
                }
                if (i < j) {
                  hasNextEvent = true;
                }
              }
            }
          }
        }
      }
      let span = 0;
      const spans: number[] = Object.values(spanMaps);
      if (spans.length > 0) {
        span = Math.max(...spans);
      }

      let position = 0;
      const positions: number[] = Object.values(positionMaps);
      if (positions.length > 0) {
        position = Math.max(...positions);
      }
      let xRatio = position === 0 ? 0 : position / (span + 1);

      let xSpanRatio = 1;
      if (span > 0) {
        xSpanRatio = 1 / (span + 1);
      }

      if (!hasNextEvent) {
        // 最後のセルの幅を最大限に広げる
        const spanRatios: number[] = Object.values(spanRatioMaps);
        const ratios: number[] = Object.values(ratioMaps);
        if (spanRatios.length > 0 && ratios.length > 0) {
          const spanRatioMax = Math.max(...spanRatios);
          const ratioMax = Math.max(...ratios);
          xSpanRatio = 1 - spanRatioMax - ratioMax;
          xRatio = ratioMax + spanRatioMax;
        }
      }
      layoutedListItems[i].xSpanRatio = xSpanRatio;
      layoutedListItems[i].xRatio = xRatio;
      layoutedListItems[i].xMargin = xRatio === 0 ? override : 0;
    }
    setEvents(layoutedListItems);
  }, [
    state.timeZone,
    schedules,
    props.startedAt,
    state.rulerStep,
    state.rulerMinHour,
    state.rulerMaxHour,
    state.showRange,
    state.isShowSameEventMerge,
    state.isShowEventOverlap,
    state.creatingEvent,
    state.hiddenWeekdays,
  ]);

  useEffect(() => {
    if (isUseCache) return;
    const gs = props.startedAt.toMillis();
    setPersistentItem(
      `${state.cachePrefix}:schedules-day-${state.showRange.day}-${gs}`,
      schedules,
      defaultCacheExpiration,
    );
  }, [
    isUseCache,
    schedules,
    props.startedAt,
    state.showRange,
    state.cachePrefix,
  ]);

  const onEventDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      // TODO
      return;

      // e.dataTransfer.effectAllowed = 'move';
      // const { eventId } = e.currentTarget.dataset;
      // if (!eventId) return;
      // const event = events.find((e) => e.eventId === eventId);
      // if (!event) return;
      // setDraggingEvent(event);
      // setDraggingGhostEvent(event);
      // disableEvents(event.eventId);
    },
    [events],
  );

  const disableEvents = (dragEventId: string) => {
    const elements = document.getElementsByClassName('js-events');
    for (let index = 0; index < elements.length; index++) {
      const element = elements[index] as HTMLElement;
      const { eventId } = element.dataset;
      if (eventId === dragEventId) {
        setTimeout(() => {
          element.style.height = '1px';
        }, 100);
      } else {
        element.style.pointerEvents = 'none';
        element.style.userSelect = 'none';
      }
    }
  };

  const onEventDragHandlerMove = useCallback(
    (e: DragEvent<HTMLDivElement>, direction: 'bottom') => {
      e.dataTransfer.effectAllowed = 'move';
      e.stopPropagation();
      const { eventId } = e.currentTarget.dataset;
      if (!eventId) return;
      const event = events.find((e) => e.uid === eventId);
      if (!event) return;
      setDraggingEvent(event);
      setDraggingGhostEvent(event);
      setDragDirection(direction);
      disableEvents(event.uid);
    },
    [events],
  );

  useEffect(() => {
    if (!isDropped) return;
    setDraggingEvent(null);
    setDraggingGhostEvent(null);
    setDragDirection(null);
    const elements = document.getElementsByClassName('js-events');
    for (let index = 0; index < elements.length; index++) {
      const element = elements[index] as HTMLElement;
      const { eventId } = element.dataset;
      if (eventId === draggingEvent?.uid) {
        element.style.height = '';
      } else {
        element.style.pointerEvents = 'auto';
        element.style.userSelect = 'auto';
      }
    }
    setIsDropped(false);
  }, [isDropped]);

  const onEventDrop = (e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsDropped(true);

    if (!draggingEvent) return;
    for (let index = 0; index < schedules.length; index++) {
      const schedule = schedules[index];
      if (schedule.isRecurringEvent) {
        console.log('recurring event');
      } else {
        if (schedule.uid === draggingEvent.eventId) {
          schedule.startedAt = draggingEvent.startedAt.toISO();
          schedule.endedAt = draggingEvent.endedAt.toISO();
          schedules[index] = schedule;
          if (typeof props.onUpdateSchedule === 'function') {
            props.onUpdateSchedule(schedule);
          }
          break;
        }
      }
    }
    dispatch(setSchedules(schedules));
  };

  const onEventDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      const { frameId } = e.currentTarget.dataset;
      if (!frameId) return;
      if (!draggingEvent) return;
      const frameIndex = Number(frameId);
      const frame = props.frames[frameIndex];

      const event = events.find((e) => e.uid === draggingEvent.eventId);
      if (!event) return;

      if (dragDirection === 'bottom') {
        let ySpan = frame.y - event.y;
        if (ySpan >= 0) {
          setDraggingEvent({
            ...draggingEvent,
            ySpan: ySpan + 1,
            endedAt: frame.date.plus({ minutes: state.rulerStep }),
          });
        } else {
          ySpan = event.y - frame.y;
          setDraggingEvent({
            ...draggingEvent,
            ySpan: ySpan + 1,
            startedAt: frame.date,
          });
        }
      } else {
        const endedAt = draggingEvent.endedAt.plus({
          milliseconds:
            frame.date.toMillis() - draggingEvent.startedAt.toMillis(),
        });
        setDraggingEvent({
          ...draggingEvent,
          x: frame.x,
          y: frame.y,
          startedAt: frame.date,
          endedAt,
        });
      }
    },
    [draggingEvent, props.frames, events, dragDirection],
  );

  const onEventDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const onEventDragEnd = useCallback((e: DragEvent<HTMLDivElement>) => {}, []);

  const onClickAddSlot = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      const { slotId } = e.currentTarget.dataset;

      dispatch(setSelectedSlots([]));
      dispatch(setSelectedGroups([]));
      dispatch(setCreatingEvent(null));

      const startIndex = Number(slotId);
      const duration = 60;
      const yStep = duration / state.rulerStep;
      let max = startIndex + yStep;
      if (yStep * state.rulerStep < state.duration) {
        max = startIndex + state.duration / state.rulerStep;
      }
      const slots: SelectedSlot[] = [];
      for (let index = startIndex; index < max; index++) {
        if ((index - startIndex) % yStep !== 0) continue;
        const frame = props.frames[index];
        if (!frame) break;
        if (frame.y + yStep > state.times.length) {
          return;
        }
        const slot = {
          x: frame.x,
          y: frame.y,
          date: frame.date,
          ySpan: yStep,
          isAllDay: false,
        };
        slots.push(slot);
      }
      const sorts = [...state.selectedSlots, ...slots].sort(
        (i, j) => i.date.toMillis() - j.date.toMillis(),
      );
      dispatch(setSelectedSlots(sorts));
    },
    [state.selectedSlots, props.frames, state.rulerStep],
  );

  return {
    state: {
      events,
      draggingGhostEvent,
      draggingEvent,
      dragDirection,
    },
    actions: {
      onEventDragStart,
      onEventDragEnter,
      onEventDragOver,
      onEventDrop,
      onEventDragEnd,
      onClickAddSlot,
      onEventDragHandlerMove,
    },
  };
}
